import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useEstatesParentDetail, useEstatesParentsLookups, useSaveEstatesParent } from "./useEstatesParents";
import { parseEstatesFromForm } from "@/utils/estateFilterAccess";
import { base_object } from "@/pages/Maintainers/EstatesParents/estates-parents.constants";

export function useEstatesParentsForm({ option, slug, onClose, filters }) {
	const isOpen = option === "crear" || option === "editar";
	const isEditMode = option === "editar";
	const modalTitle = useMemo(() => (isEditMode ? "Editar Campo" : "Crear Campo"), [isEditMode]);

	const { mutate: saveEstatesParent, isLoading: isLoadingSave } = useSaveEstatesParent();
	const { data: lookups, isLoading: isLoadingLookups } = useEstatesParentsLookups();

	const canLoadDetail = isEditMode && !!slug;
	const { data: response, isLoading: isLoadingElement, isFetching: isFetchingElement } = useEstatesParentDetail(slug, canLoadDetail);

	const estates = useMemo(() => lookups?.data.estates ?? [], [lookups]);
	const regions = useMemo(() => lookups?.data.regions ?? [], [lookups]);

	const [formState, setFormState] = useState({ ...base_object });
	const [formErrors, setFormErrors] = useState({});
	const [localities, setLocalities] = useState([]);

	useEffect(() => {
		if (isEditMode && response?.data) {
			const detail = response.data;
			const day_selection = detail.days ? detail.days.length > 0 : false;

			setFormState((prev) => ({
				...prev,
				...detail,
				estates: detail.estates ?? [],
				region: detail.region ?? detail.region_id ?? null,
				locality: detail.locality ?? detail.locality_id ?? null,
				day_selection: day_selection,
				days: detail.days ?? [],
			}));
		}
	}, [isEditMode, response]);

	useEffect(() => {
		if (formState.region && regions.length > 0) {
			const selectedRegion = regions.find((r) => r.value === formState.region);
			setLocalities(selectedRegion?.localities ?? []);
		} else {
			setLocalities([]);
		}
	}, [formState.region, regions]);

	useEffect(() => {
		if (!isOpen) {
			setFormState({ ...base_object });
			setFormErrors({});
			setLocalities([]);
		}
	}, [isOpen]);

	const handleRegionChange = (value) => {
		setFormState((prev) => ({ ...prev, region: value ?? null, locality: null }));
		setFormErrors((prev) => ({ ...prev, region: undefined }));
		const selectedRegion = regions.find((r) => r.value === value);
		setLocalities(selectedRegion?.localities ?? []);
	};

	const validateForm = (state) => {
		const errors = {};
		if (!state.name?.trim()) errors.name = "Requerido";
		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const errors = validateForm(formState);
		setFormErrors(errors);
		if (Object.keys(errors).length) return;

		const { existingIds, newNames } = parseEstatesFromForm(formState.estates);

		const formData = {
			...(isEditMode && { id: formState.id }),
			name: formState.name?.trim() || "",
			estates: existingIds,
			new_estate_names: newNames,
			hectares: String(formState.hectares) || "",
			time_open: formState.time_open || "",
			time_close: formState.time_close || "",
			notification_range: formState.notification_range || "",
			day_selection: formState.day_selection ?? false,
			days: formState.days ?? [],
			region: formState.region != null && formState.region !== "" ? Number(formState.region) : null,
			locality: formState.locality != null && formState.locality !== "" ? Number(formState.locality) : null,
		};

		saveEstatesParent(
			{ form: formData, filters },
			{
				onSuccess: () => {
					toast.success(isEditMode ? "Campo actualizado" : "Campo creado");
					onClose();
				},
				onError: (error) => {
					toast.error(error?.response?.data?.error || error?.response?.data?.message || "Error al guardar el campo");
				},
			},
		);
	};

	return {
		isOpen,
		isEditMode,
		modalTitle,
		isLoadingLookups,
		isLoadingSave,
		isLoadingElement,
		isFetchingElement,
		estates,
		regions,
		formState,
		setFormState,
		formErrors,
		setFormErrors,
		localities,
		handleRegionChange,
		handleSubmit,
	};
}
